import type { Alpine } from 'alpinejs';

type ConversionProgram = { auth: string; name: string; futureClose: boolean; eventDates: string; opens: string; closes: string };

function isProgram(value: unknown): value is ConversionProgram {
    return typeof value === 'object' && value !== null
        && 'auth' in value && typeof value.auth === 'string'
        && 'name' in value && typeof value.name === 'string'
        && 'futureClose' in value && typeof value.futureClose === 'boolean'
        && 'eventDates' in value && typeof value.eventDates === 'string'
        && 'opens' in value && typeof value.opens === 'string'
        && 'closes' in value && typeof value.closes === 'string';
}

export function registerProgramConversion(Alpine: Alpine) {
    Alpine.data('programConversion', (programs: ConversionProgram[], selected: string[], sourceYear: string, targetYear: string, targetYears: string[]) => {
        let request: AbortController | undefined;
        let generation = 0;
        return {
            programs, sourceYear, targetYear, targetYears,
            selected,
            search: '',
            futureOnly: false,
            loading: false,
            loadError: '',
            init() {
                this.$watch('search', () => this.keepVisibleSelections());
                this.$watch('futureOnly', () => this.keepVisibleSelections());
            },
            get visibleIds(): string[] {
                const search = this.search.toLowerCase();
                return this.programs.filter(program => program.name.toLowerCase().includes(search)
                    && (!this.futureOnly || program.futureClose)).map(program => program.auth);
            },
            keepVisibleSelections() {
                const visible = new Set(this.visibleIds);
                this.selected = this.selected.filter(auth => visible.has(auth));
            },
            selectAll() {
                this.selected = this.visibleIds;
            },
            get canReview(): boolean {
                return !this.loading && !this.loadError && this.selected.length > 0
                    && this.targetYear !== '' && this.targetYear !== this.sourceYear;
            },
            async loadPrograms() {
                const current = ++generation;
                const year = this.sourceYear;
                request?.abort();
                request = new AbortController();
                this.selected = [];
                this.programs = [];
                this.loading = true;
                this.loadError = '';
                if (this.targetYear === year) this.targetYear = '';
                try {
                    const response = await fetch('/api/programs/conversion?source_year=' + encodeURIComponent(year), {
                        credentials: 'same-origin', cache: 'no-store', signal: request.signal,
                    });
                    if (!response.ok) throw new Error(response.status === 401 || response.status === 403
                        ? 'Your session expired or you no longer have permission. Reload this page.'
                        : 'Programs could not be loaded. Please try again.');
                    const data: unknown = await response.json();
                    if (current !== generation) return;
                    if (typeof data !== 'object' || data === null || !('sourceYear' in data) || data.sourceYear !== year
                        || !('programs' in data) || !Array.isArray(data.programs) || !data.programs.every(isProgram)) {
                        throw new Error('Programs could not be loaded. Please try again.');
                    }
                    this.programs = data.programs;
                } catch (error) {
                    if (current !== generation) return;
                    this.loadError = error instanceof Error ? error.message : 'Programs could not be loaded. Please try again.';
                } finally {
                    if (current === generation) this.loading = false;
                }
            },
            destroy() { generation++; request?.abort(); },
        };
    });
}
